import CargosServicios from '@/components/cd2b/CargosServicios'
import DescuentoInput from '@/components/cd2b/DescuentoInput'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { MODALIDADES } from './paquete'
import { modalidadEntregaCatalogo } from '@/lib/catalogoContadorModalidad'
import { fmtMoneda } from '@/lib/cd2bPricing'
import { opcionesDeCiclo } from '@/lib/cicloPago'
import { cn } from '@/lib/utils'

/**
 * Paso 3 — Cobro y servicios: lo que se cobra aparte del paquete (servicios, cargos únicos,
 * descuento), en qué moneda, y dónde viven los datos. La modalidad va aquí porque decide si hay
 * empresa demo: sin «Nube gestionada» el paso 4 no puede crearla.
 */
export default function PasoCobro({ c }) {
  const { form, setForm, catalogo, quote } = c

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-h3">Servicios y cargos</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <CargosServicios
            catalogo={catalogo}
            cargos={form.cargosAdicionales}
            setCargos={(nuevos) => setForm((f) => ({ ...f, cargosAdicionales: nuevos }))}
            moneda={form.moneda}
          />
          <DescuentoInput
            descuento={form.descuento}
            setDescuento={(d) => setForm((f) => ({ ...f, descuento: d }))}
            moneda={form.moneda}
            montoDescuento={quote.descuento?.monto || 0}
            precioFull={quote.descuento?.precioFull || 0}
          />
          <div className="sm:max-w-[12rem]">
            <Label htmlFor="cot-moneda">Moneda</Label>
            <Input id="cot-moneda" value={form.moneda} onChange={(e) => setForm({ ...form, moneda: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-h3">Cómo paga el cliente</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Forma de pago">
            {opcionesDeCiclo({ mensual: quote.totalMensual, anual: c.comision?.anual }).map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={form.ciclo === o.value}
                disabled={o.deshabilitada}
                onClick={() => setForm({ ...form, ciclo: o.value })}
                className={cn(
                  'rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                  form.ciclo === o.value ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30' : 'border-line bg-surface hover:bg-surface-muted',
                )}
              >
                <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50">{o.titulo}</p>
                <p className="text-body-sm tabular-nums text-neutral-800 dark:text-neutral-200">
                  {o.monto != null ? fmtMoneda(o.monto, form.moneda) : '—'}{o.periodo}
                </p>
                {o.nota && <p className="text-caption text-subtle">{o.nota(form.moneda, fmtMoneda)}</p>}
              </button>
            ))}
          </div>
          {form.ciclo === 'anual' && form.descuento && Number(form.descuento.mesesPromo) > 0 && (
            <Alert variant="warning">
              <AlertDescription>
                Una promoción por meses no se combina con el pago anual con tarjeta internacional. Quita los meses de la promoción o elige pago mensual.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-h3">Arquitectura de datos</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="sm:max-w-sm">
            <Label>Modalidad</Label>
            <Select
              value={form.modalidad}
              onValueChange={(v) => {
                setForm({ ...form, modalidad: v })
                // Al salir de la nube el asistente del catálogo se desmonta y, si se vuelve, nace
                // sin contestar. La respuesta guardada se suelta a la vez (B1).
                if (!modalidadEntregaCatalogo(v)) c.setDeclaracionCatalogo(null)
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODALIDADES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.modalidad !== 'nube' && (
              <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
                La empresa demo que prueba el dueño solo existe en «Nube gestionada».
              </p>
            )}
          </div>
          {form.modalidad === 'nube' && (
            <>
              <Alert variant="warning">
                <AlertDescription>
                  Nube asistida: la base de datos de la empresa debe estar creada antes de activarla. El token
                  de acceso se captura al activar (no se guarda en claro).
                </AlertDescription>
              </Alert>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="cot-host">Host del servidor nube</Label>
                  <Input id="cot-host" value={form.instanciaNube.host}
                    onChange={(e) => setForm({ ...form, instanciaNube: { ...form.instanciaNube, host: e.target.value } })}
                    placeholder="https://2b-data1.coredeskpro.com" />
                </div>
                <div>
                  <Label htmlFor="cot-tenant">Tenant ID</Label>
                  <Input id="cot-tenant" value={form.instanciaNube.tenantId}
                    onChange={(e) => setForm({ ...form, instanciaNube: { ...form.instanciaNube, tenantId: e.target.value } })} />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
